import { useState, useEffect } from "react";
import DataTable from "../components/common/DataTable";
import TableHeader from "../components/common/TableHeader";
import {toTimestamp} from '../functions';
import { useListPagePreferences } from "../hooks/useListPagePreferences";


const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
const userRole = localStorage.getItem("GeopUserRole") || "user";
const isAdmin = userRole === "admin";

export default function DriverLicenseList() {
  // UI / data state
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(0);

  // search / sort state
  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState<string>(isAdmin ? "id" : "name");
  const [sortColumn, setSortColumn] = useState("id_conducteur");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("ASC");
  const { ready: listPreferencesReady } = useListPagePreferences({
    pageKey: "administrative-driver-licenses",
    pageSize: limit, setPageSize: setLimit,
    searchType, setSearchType,
    searchText: searchValue, setSearchText: setSearchValue,
    sortColumn, setSortColumn,
    sortDirection, setSortDirection,
  });
  // ===== Columns =====
  const columns = [
    ...(isAdmin ? [{ key: "id_conducteur", label: "ID" }] : []),
    { key: "nom_conducteur", label: "Conducteur" },
    { key: "numero_permis_conducteur", label: "Numéro de permis" },
    { key: "premis_conducteur", label: "Type de permis" },
    { key: "date_delivrance_permis_conducteur", label: "Date de délivrance" },
    { key: "date_expir_permis_conducteur", label: "Date d’expiration" },
  ];

  // ===== Search options =====
  const searchOptions = [
    ...(isAdmin
      ? [{ label: "Driver ID", value: "id", column: "id_conducteur" }]
      : []),
    { label: "Driver name", value: "name", column: "nom_conducteur" },
    { label: "License number", value: "license", column: "numero_permis_conducteur" },
    { label: "License type", value: "type", column: "premis_conducteur" },
    { label: "Date délivrance", value: "delivery", column: "date_delivrance_permis_conducteur" },
    { label: "Date expiration", value: "expiration", column: "date_expir_permis_conducteur" }
  ];

  // ===== Fetch page =====
  const fetchDriverLicensePage = async (
    limitValue = limit,
    page = currentPage,
    search = searchValue,
    type = searchType,
    column = sortColumn,
    sort = sortDirection
  ) => {
    const id_user = localStorage.getItem("GeopUserID") || "";
    const role = localStorage.getItem("GeopUserRole") || "user";

    setLoading(true);
    try {
      const body: any = {
          limitValue,
          currentPage: page,
          search,
          type,
          column,
          sort,
          role,
        };
        if (id_user) body.id_user = id_user; // include id_user only when present
      const res = await fetch(`${backendUrl}/api/geop/driverLicense/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "include",
      });

      if (!res.ok) throw new Error("Failed to fetch driver licenses");

      const data = await res.json();

      // combine nom + prenom into one column
      const formatted = Array.isArray(data)
        ? data.map((row: any) => {
            const safeDate = (d: any) => {
              if (d === null || d === undefined || d === "") return "-";
              const s = String(d);
              if (/^0{4}-0{2}-0{2}/.test(s)) return "-"; // MySQL zero-date (0000-00-00)
              if (isNaN(new Date(s).getTime())) return "-";
              return toTimestamp(s);
            };

            return {
              ...row,
              nom_conducteur: `${row.nom_conducteur ?? ""} ${row.prenom_conducteur ?? ""}`.trim(),
              date_delivrance_permis_conducteur: safeDate(row.date_delivrance_permis_conducteur),
              date_expir_permis_conducteur: safeDate(row.date_expir_permis_conducteur),
            };
          })
        : [];

      setRows(formatted);
    } catch (err) {
      console.error("Error fetching driver licenses:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // ===== Fetch total =====
  const fetchDriverLicenseTotal = async (search = "", type = "", limitValue = limit) => {
    const id_user = localStorage.getItem("GeopUserID") || "";
    const role = localStorage.getItem("GeopUserRole") || "user";

    try {
      const body: any={ role, search, type };
      if (id_user) body.id_user = id_user;
      
      const res = await fetch(`${backendUrl}/api/geop/driverLicense/totalpage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "include",
      });

      if (!res.ok) throw new Error("Failed to fetch total");

      const json = await res.json();
      const totalCount =
        Array.isArray(json) && json[0]?.count ? json[0].count : json?.count ?? 0;

      setTotal(totalCount);
      setPageCount(Math.ceil(totalCount / limitValue));
    } catch (err) {
      console.error("Error fetching total:", err);
      setTotal(0);
      setPageCount(0);
    }
  };

  // ===== Initial load =====
  useEffect(() => {
    if (!listPreferencesReady) return;
    const timeout = window.setTimeout(() => {
      void fetchDriverLicenseTotal(searchValue, searchType, limit);
      void fetchDriverLicensePage(limit, currentPage, searchValue, searchType, sortColumn, sortDirection);
    }, 300);

    return () => window.clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit, searchValue, searchType, sortColumn, sortDirection, listPreferencesReady]);

  // ===== Search handlers =====
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleSearchTypeChange = (typeVal: string) => {
    setSearchType(typeVal);
    setSearchValue("");
    setCurrentPage(1);
  };

  const handleResetSearch = () => {
    setSearchValue("");
    setCurrentPage(1);
  };

  // ===== Pagination =====
  const handlePageClick = (data: any) => {
    const newPage = data.selected + 1;
    setCurrentPage(newPage);
    window.scrollTo(0, 0);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  // ===== Sorting =====
  const handleSort = (columnKey: string) => {
    const columnMap: Record<string, string> = {
      id_conducteur: "id_conducteur",
      nom_conducteur: "nom_conducteur",
      numero_permis_conducteur: "numero_permis_conducteur",
      premis_conducteur: "premis_conducteur",
      date_delivrance_permis_conducteur: "date_delivrance_permis_conducteur",
      date_expir_permis_conducteur: "date_expir_permis_conducteur",
    };

    const backendColumn = columnMap[columnKey] || columnKey;
    const nextSort = sortDirection === "ASC" ? "DESC" : "ASC";
    
    setSortColumn(backendColumn);
    setSortDirection(nextSort);
    setCurrentPage(1);
  };

  return (
    <div className="p-3">
      <TableHeader
        title="Permis de conduire"
        totalCount={total}
        searchValue={searchValue}
        searchOptions={searchOptions}
        onSearchChange={handleSearchChange}
        onSearchTypeChange={handleSearchTypeChange}
        onResetSearch={handleResetSearch}
      />

      <DataTable
        data={rows}
        columns={columns}
        loading={loading}
        totalCount={total}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSortChange={handleSort}
        pageCount={pageCount}
        currentPage={currentPage}
        limit={limit}
        onPageChange={(page) => handlePageClick({ selected: page - 1 })}
        onLimitChange={handleLimitChange}
        rowIdKey="id_conducteur"
      />
    </div>
  );
}
